import { useCoAgent } from "@copilotkit/react-core"
import { useAgent } from "@copilotkit/react-core/v2"
import { useThreadContext } from "@/providers/ThreadProvider"
import { isMockReplay } from "@/features/oio/mock/mockMode"
import { useMockOIOAgent } from "@/features/oio/mock/useMockRun"

/**
 * Mirrors the AG-UI-streamed fields from OIOGraphState (Python backend).
 * `useCoAgent` reads live state during generation; it does NOT auto-restore
 * from the LangGraph Postgres checkpoint on page refresh for a generic AG-UI
 * HttpAgent backend — see docs/copilotkit-integration-analysis.md §1b.
 */
export interface OIOAgentState {
  /**
   * The nitiassist thread id this draft belongs to. Injected by the frontend at
   * run start (OIOWorkspace) and read by the backend (ingest/compile, which
   * prefer typed_state.thread_id) so the pipeline PATCHes draft_status /
   * artifactBlobUrl back to the CORRECT nitiassist thread on completion — even
   * though CopilotKit runs the LangGraph graph under its own generated threadId,
   * not the case id. This is what lets a reconnecting client (close tab / sleep /
   * reopen) find the finished order via thread metadata + blob.
   */
  thread_id: string
  /** Lifecycle status of the OIO draft — authoritative source from LangGraph checkpoint */
  draft_status: string
  /** Stages that have fully completed, e.g. ["ingest", "alignment"] */
  completed_stages: string[]
  /** The stage currently executing, null when idle */
  current_stage: string | null
  /** Optional granular hint, e.g. "Allegation A-1" or "Section 5.2" */
  current_substage: string | null
  /** The final compiled OIO markdown — set by compile_oio_node */
  oio_draft: string | null
  /**
   * Blob URL pointing to the uploaded draft in object storage.
   * Emitted by compile_oio_node after generation and stored in the
   * LangGraph checkpoint, so it survives page refresh. Used by the
   * frontend to save user edits back via /api/v1/storage/overwrite_text.
   */
  artifact_blob_url: string | null
  /** Monotonic draft version emitted by the backend for save/overwrite coordination. */
  draft_revision: number | null
  /**
   * JSON-serialised array of per-allegation findings built by
   * speaking_order_check_node.  Stored in the checkpoint so the Analysis
   * tab auto-populates on page refresh.  Parsed and mapped to the
   * Allegation[] type by the useEffect in OIOWorkspaceContent.
   */
  findings_summary_json: string | null
  /**
   * JSON-serialised array of AlignedAllegation, written at the alignment node
   * boundary (so it propagates reliably, before the analysis fan-out). Lets the
   * UI surface the real, case-specific allegations early — both as live
   * "thinking" during analysis and as the Analysis tab's initial rows.
   *
   * Read-only on the frontend: it is intentionally omitted from INITIAL_STATE so
   * CopilotKit never echoes it back to the backend at run start (the backend
   * field is a non-nullable list[str] and would reject a `null`). We only ever
   * consume the value the backend emits.
   */
  aligned_allegations_json?: string[] | null
  /**
   * JSON-serialised DraftReviewReport — the second artifact of the two-artifact
   * split (validity/threshold findings, citation verification states, gate
   * results). Emitted by compile_oio_node; never rendered into the order text.
   * Feeds the citation-review chips + modal. Omitted from INITIAL_STATE for the
   * same echo-back reason as aligned_allegations_json.
   */
  review_report_json?: string | null
  /**
   * Procedural fact recorded at ingest: 'received' | 'not_received' ('' on
   * legacy checkpoints from before the reply became optional). 'not_received'
   * means no reply to the SCN exists and the order proceeds ex-parte on the
   * record. Read-only on the frontend and omitted from INITIAL_STATE (the
   * backend field is a non-nullable str) — same echo-back reason as
   * aligned_allegations_json.
   */
  reply_status?: string | null
}

const INITIAL_STATE: OIOAgentState = {
  thread_id: "",
  draft_status: "draft_not_started",
  completed_stages: [],
  current_stage: null,
  current_substage: null,
  oio_draft: null,
  artifact_blob_url: null,
  draft_revision: null,
  findings_summary_json: null,
}

/** Return contract of useOIOAgent — shared with the mock-replay implementation. */
export interface UseOIOAgentResult {
  state: OIOAgentState
  setState: (updater: OIOAgentState | ((prev: OIOAgentState | undefined) => OIOAgentState)) => void
  run: () => Promise<unknown>
  running: boolean
}

/**
 * Single bridge between the CopilotKit/AG-UI agent state and the UI.
 *
 * Returns `state`/`setState` (the normalised agent state and the CopilotKit
 * updater) plus `run`/`running`, which useDraftStreamReconnect
 * (features/drafting/hooks/) uses to reattach to an in-progress backend run
 * on mount — CopilotKit's own connectAgent() cannot do this for a generic
 * AG-UI HttpAgent backend (it always throws AGUIConnectNotImplementedError,
 * silently swallowed; see docs/copilotkit-integration-analysis.md §1b).
 * Consumers that need to push state into the agent (e.g. useRewriteCompletion)
 * must use the `setState` returned here rather than calling useCoAgent("oio")
 * a second time, which would create a duplicate subscription that can cause
 * double-render cycles.
 *
 * MOCK REPLAY (?mock=aditya): the state source swaps to the scripted fixture
 * replay (src/features/oio/mock/) so EVERY consumer of this hook — the
 * workspace, useCaseDraftStatus, DraftGenerationPane — follows the replay
 * without further changes. isMockReplay() is latched on first call, so the
 * branch below is constant for the lifetime of the page and hook order can
 * never change between renders. With the flag off, the real path runs
 * unchanged.
 */
export function useOIOAgent(): UseOIOAgentResult {
  if (isMockReplay()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- isMockReplay() is latched per page load; the branch never changes between renders
    return useMockOIOAgent()
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks -- see above
  return useRealOIOAgent()
}

function useRealOIOAgent(): UseOIOAgentResult {
  const { activeThread } = useThreadContext()
  const persistedDraftStatus = activeThread?.metadata?.draft_status as string | undefined
  const isDraftDone =
    persistedDraftStatus === "draft_completed" ||
    persistedDraftStatus === "draft_failed_terminal"

  const coAgent = useCoAgent<OIOAgentState>({
    name: "oio",
    initialState: INITIAL_STATE,
  })
  // useCoAgent 1.61 returns `agent.runAgent` as an unbound method reference.
  // Call it through the underlying agent so its `this` remains the agent
  // instance (runAgent internally uses prepareRunAgentInput).
  const { agent: underlyingAgent } = useAgent({ agentId: "oio" })

  // When the draft is already complete or failed terminal, do not fire a new /stream run.
  // CopilotKit's connectAgent fires useCoAgent's run() on subscription —
  // replacing with a no-op prevents the RunCoordinator from seeing the
  // request and replaying the old terminal RUN_ERROR.
  const run = isDraftDone
    ? async () => {}
    : () => (underlyingAgent ? underlyingAgent.runAgent() : coAgent.run())

  const normalisedState: OIOAgentState = {
    ...INITIAL_STATE,
    ...(coAgent.state ?? {}),
    thread_id: coAgent.state?.thread_id ?? "",
    draft_status: coAgent.state?.draft_status ?? "draft_not_started",
    completed_stages: Array.isArray(coAgent.state?.completed_stages) ? coAgent.state.completed_stages : [],
    current_stage: coAgent.state?.current_stage ?? null,
    current_substage: coAgent.state?.current_substage ?? null,
    oio_draft: coAgent.state?.oio_draft ?? null,
    artifact_blob_url: coAgent.state?.artifact_blob_url ?? null,
    draft_revision: typeof coAgent.state?.draft_revision === "number" ? coAgent.state.draft_revision : null,
    findings_summary_json: coAgent.state?.findings_summary_json ?? null,
    aligned_allegations_json: Array.isArray(coAgent.state?.aligned_allegations_json)
      ? coAgent.state.aligned_allegations_json
      : null,
    review_report_json:
      typeof coAgent.state?.review_report_json === "string"
        ? coAgent.state.review_report_json
        : null,
    reply_status:
      typeof coAgent.state?.reply_status === "string" ? coAgent.state.reply_status : null,
  }

  return { state: normalisedState, setState: coAgent.setState, run, running: coAgent.running }
}
